// @ts-nocheck
/**
 * Vuex Compatibility Layer
 *
 * This file provides a Vuex store that wraps Pinia stores,
 * allowing components using mapGetters/mapActions to work
 * while we gradually migrate them to Pinia.
 *
 * NOTE: TypeScript checking is disabled for this file as it's a temporary
 * compatibility bridge. Many getters/actions referenced here may not exist
 * in the new Pinia stores but are still used by legacy Vuex components.
 * Components should be gradually migrated to use Pinia directly.
 */

import { createStore } from 'vuex'
import { useAccountsStore } from './stores/accounts'
import { useLoginStore } from './stores/login'
import { useRegisterStore } from './stores/register'
import { useNetworkStore } from './stores/network'
import { useRolesStore } from './stores/roles'
import { useTransactionsStore } from './stores/transactions'
import { useDepartmentsStore } from './stores/departments'
import { useOccupationsStore } from './stores/occupations'
import { useCountriesStore } from './stores/countries'
import { useUserManualsStore } from './stores/user-manuals'
import { useInsolvencySettingsStore } from './stores/insolvency-settings'
import { useServiceCostsStore } from './stores/service-costs'
import { useTaxHeadsStore } from './stores/tax-heads'
import { usePrnManagementStore } from './stores/prn-management'
import { useSettingsStore } from './stores/settings'
import { useNameSearchStore } from './stores/name-search'
import { useReservationsStore } from './stores/reservations'
import { useIsicStore } from './stores/isic'
import { useDictionaryStore } from './stores/dictionary'
import { useRejectionQueryMessagesStore } from './stores/rejection-query-messages'
import { useBusinessFileStore } from './stores/business-file'
import { useEntityDocumentsStore } from './stores/entity-documents'
import { useLegalDocumentsRegistryStore } from './stores/legal-documents-registry'
import { usePractitionersRegistryStore } from './stores/practitioners-registry'
import { useBusinessRegistryStore } from './stores/business-registry'
import { useInternalProcessesStore } from './stores/internal-processes'
import { useIntegrationsStore } from './stores/integrations'

// Helper functions to get stores with any type for compatibility
const getAccountsStore = () => useAccountsStore() as any
const getLoginStore = () => useLoginStore() as any
const getRegisterStore = () => useRegisterStore() as any
const getNetworkStore = () => useNetworkStore() as any
const getRolesStore = () => useRolesStore() as any
const getTransactionsStore = () => useTransactionsStore() as any
const getDepartmentsStore = () => useDepartmentsStore() as any
const getOccupationsStore = () => useOccupationsStore() as any
const getCountriesStore = () => useCountriesStore() as any
const getUserManualsStore = () => useUserManualsStore() as any
const getInsolvencySettingsStore = () => useInsolvencySettingsStore() as any
const getServiceCostsStore = () => useServiceCostsStore() as any
const getTaxHeadsStore = () => useTaxHeadsStore() as any
const getPrnManagementStore = () => usePrnManagementStore() as any
const getSettingsStore = () => useSettingsStore() as any
const getNameSearchStore = () => useNameSearchStore() as any
const getReservationsStore = () => useReservationsStore() as any
const getIsicStore = () => useIsicStore() as any
const getDictionaryStore = () => useDictionaryStore() as any
const getRejectionQueryMessagesStore = () => useRejectionQueryMessagesStore() as any
const getBusinessFileStore = () => useBusinessFileStore() as any
const getEntityDocumentsStore = () => useEntityDocumentsStore() as any
const getLegalDocumentsRegistryStore = () => useLegalDocumentsRegistryStore() as any
const getPractitionersRegistryStore = () => usePractitionersRegistryStore() as any
const getBusinessRegistryStore = () => useBusinessRegistryStore() as any
const getInternalProcessesStore = () => useInternalProcessesStore() as any
const getIntegrationsStore = () => useIntegrationsStore() as any

// Create a Vuex store that acts as a proxy to Pinia stores
export default createStore({
  state: {},
  getters: {
    // Login Store Getters
    getLoginData: () => useLoginStore().getLoginData,
    getLoginError: () => useLoginStore().getLoginError,
    getOtpSuccess: () => useLoginStore().getOtpSuccess,
    getUserLogoutSuccess: () => useLoginStore().getUserLogoutSuccess,
    getRequestPassword: () => useLoginStore().getRequestPassword,
    getNewPassword: () => useLoginStore().getNewPassword,
    getSetToken: () => useLoginStore().getSetToken,
    getUserLogout: () => useLoginStore().getUserLogout,
    getUserProfileData: () => useLoginStore().getUserProfileData,
    getUserProfile: () => useLoginStore().getUserProfileData,

    // Register Store Getters
    getNewUser: () => (getRegisterStore() as any).getNewUser,
    getUserError: () => (getRegisterStore() as any).getUserError,
    getRegistrationData: () => useRegisterStore().getRegistrationData,
    getRegisterError: () => useRegisterStore().getRegisterError,

    // Network Store Getters
    getNetworkStatus: () => useNetworkStore().getNetworkStatus,

    // Roles Store Getters
    getRoles: () => (getRolesStore() as any).getRoles,
    getRole: () => (getRolesStore() as any).getRole,
    getRoleError: () => (getRolesStore() as any).getRoleError,
    getPermissions: () => (getRolesStore() as any).getPermissions,
    getPermission: () => (getRolesStore() as any).getPermission,
    getPermissionError: () => (getRolesStore() as any).getPermissionError,
    getPermissionGroups: () => (getRolesStore() as any).getPermissionGroups,
    getPermissionGroup: () => (getRolesStore() as any).getPermissionGroup,
    getPermissionGroupError: () => (getRolesStore() as any).getPermissionGroupError,
    getGroupPermissions: () => (getRolesStore() as any).getGroupPermissions,
    getUserRoles: () => (getRolesStore() as any).getUserRoles,
    getUserRole: () => (getRolesStore() as any).getUserRole,
    getRoleRevoked: () => (getRolesStore() as any).getRoleRevoked,
    getRolesList: () => useRolesStore().getRolesList,
    getRoleDetails: () => useRolesStore().getRoleDetails,
    getNewRole: () => useRolesStore().getNewRole,
    getNewRoleError: () => useRolesStore().getNewRoleError,
    getPermissionList: () => useRolesStore().getPermissionList,
    getPermissionGroupsList: () => useRolesStore().getPermissionGroupsList,
    getRolesAndPermissionEdit: () => useRolesStore().getRolesAndPermissionEdit,
    getRolesAndPermissionUpdate: () => useRolesStore().getRolesAndPermissionUpdate,
    getRolesError: () => useRolesStore().getRolesError,
    getRoleRemovalSuccess: () => useRolesStore().getRoleRemovalSuccess,
    getNewPermission: () => useRolesStore().getNewPermission,
    getNewPermissionGroup: () => useRolesStore().getNewPermissionGroup,

    // Accounts Store Getters
    getOfficerAccounts: () => useAccountsStore().getOfficerAccounts,
    getUserAccounts: () => useAccountsStore().getUserAccounts,
    getMdaAccounts: () => useAccountsStore().getMdaAccounts,
    getPartnerAccounts: () => useAccountsStore().getPartnerAccounts,
    getClientAccounts: () => useAccountsStore().getClientAccounts,
    getBusinessAccounts: () => useAccountsStore().getBusinessAccounts,
    getStaffSignature: () => useAccountsStore().getStaffSignature,
    getAccountsError: () => useAccountsStore().getAccountsError,
    getUserAccount: () => useAccountsStore().getUserAccount,
    getUserAccessExtended: () => useAccountsStore().getUserAccessExtended,
    getUserAccessSuspended: () => useAccountsStore().getUserAccessSuspended,
    getUserAccessRevoked: () => useAccountsStore().getUserAccessRevoked,
    getToggleApiStatus: () => useAccountsStore().getToggleApiStatus,
    getAccessAreaList: () => useAccountsStore().getAccessAreaList,
    getNiraData: () => useAccountsStore().getNiraData,
    getUserNira: () => useAccountsStore().getUserNira,

    // Transactions Store Getters
    getTransactions: () => useTransactionsStore().getTransactions,
    getTransaction: () => useTransactionsStore().getTransaction,
    getTransactionError: () => useTransactionsStore().getTransactionError,
    getTransactionsFilter: () => useTransactionsStore().getTransactionsFilter,
    getTransactionsListError: () => useTransactionsStore().getTransactionsListError,
    getTransactionSuccess: () => useTransactionsStore().getTransactionSuccess,
    getPRNReleaseSuccess: () => useTransactionsStore().getPRNReleaseSuccess,
    getPRNReleaseError: () => useTransactionsStore().getPRNReleaseError,

    // Departments Store Getters
    getDepartments: () => useDepartmentsStore().getDepartments,
    getDepartment: () => useDepartmentsStore().getDepartment,
    getDepartmentError: () => useDepartmentsStore().getDepartmentError,

    // Occupations Store Getters
    getOccupations: () => useOccupationsStore().getOccupations,
    getOccupation: () => useOccupationsStore().getOccupation,
    getOccupationError: () => useOccupationsStore().getOccupationError,

    // Countries Store Getters
    getCountries: () => useCountriesStore().getCountries,
    getCountry: () => useCountriesStore().getCountry,
    getCountryError: () => useCountriesStore().getCountryError,

    // User Manuals Store Getters
    getUserManuals: () => useUserManualsStore().getUserManuals,
    getUserManual: () => useUserManualsStore().getUserManual,
    getUserManualError: () => useUserManualsStore().getUserManualError,

    // Insolvency Settings Store Getters
    getProfessionalBodies: () => useInsolvencySettingsStore().getProfessionalBodies,
    getProfessionalBody: () => useInsolvencySettingsStore().getProfessionalBody,
    getProfessionalQualifications: () => useInsolvencySettingsStore().getProfessionalQualifications,
    getProfessionalQualification: () => useInsolvencySettingsStore().getProfessionalQualification,
    getAcademicQualifications: () => useInsolvencySettingsStore().getAcademicQualifications,
    getAcademicQualification: () => useInsolvencySettingsStore().getAcademicQualification,
    getReasons: () => useInsolvencySettingsStore().getReasons,
    getReason: () => useInsolvencySettingsStore().getReason,
    getInsolvencyError: () => useInsolvencySettingsStore().getInsolvencyError,

    // Service Costs Store Getters
    getServiceCosts: () => useServiceCostsStore().getServiceCosts,
    getServiceCost: () => useServiceCostsStore().getServiceCost,
    getDocumentCosts: () => useServiceCostsStore().getDocumentCosts,
    getDocumentCost: () => useServiceCostsStore().getDocumentCost,
    getCurrencies: () => useServiceCostsStore().getCurrencies,
    getServiceCostError: () => useServiceCostsStore().getServiceCostError,
    getEditServiceCost: () => useServiceCostsStore().getEditServiceCost,

    // Tax Heads Store Getters
    getTaxHeads: () => useTaxHeadsStore().getTaxHeads,
    getTaxHead: () => useTaxHeadsStore().getTaxHead,
    getTaxHeadError: () => useTaxHeadsStore().getTaxHeadError,

    // PRN Management Store Getters
    getPrns: () => usePrnManagementStore().getPrns,
    getPrn: () => usePrnManagementStore().getPrn,
    getPrnDeleted: () => usePrnManagementStore().getPrnDeleted,
    getPrnError: () => usePrnManagementStore().getPrnError,
    getUsedPRNs: () => usePrnManagementStore().getUsedPRNs,
    getPRNsAdded: () => usePrnManagementStore().getPRNsAdded,
    getPRNRemoved: () => usePrnManagementStore().getPRNRemoved,
    getPRNError: () => usePrnManagementStore().getPRNError,

    // Settings Store Getters
    getMessenger: () => useSettingsStore().getMessenger,
    getSettingsError: () => useSettingsStore().getSettingsError,
    getAddress: () => useSettingsStore().getAddress,

    // Name Search Store Getters
    getNameSearch: () => useNameSearchStore().getNameSearch,
    getNameSearchError: () => useNameSearchStore().getNameSearchError,

    // Reservations Store Getters
    getReservations: () => useReservationsStore().getReservations,
    getReservation: () => useReservationsStore().getReservation,
    getReservationError: () => useReservationsStore().getReservationError,
    getReservationExpiryTimes: () => useReservationsStore().getReservationExpiryTimes,
    getAddStatus: () => useReservationsStore().getAddStatus,
    getAddError: () => useReservationsStore().getAddError,

    // ISIC Store Getters
    getIsicCategories: () => useIsicStore().getIsicCategories,
    getIsicCategory: () => useIsicStore().getIsicCategory,
    getIsicError: () => useIsicStore().getIsicError,

    // Dictionary Store Getters
    getDictionaryWords: () => useDictionaryStore().getDictionaryWords,
    getDictionaryWord: () => useDictionaryStore().getDictionaryWord,
    getDictionaryTypes: () => useDictionaryStore().getDictionaryTypes,
    getDictionaryError: () => useDictionaryStore().getDictionaryError,

    // Rejection Query Messages Store Getters
    getRejectionMessages: () => useRejectionQueryMessagesStore().getRejectionMessages,
    getRejectionMessage: () => useRejectionQueryMessagesStore().getRejectionMessage,
    getRejectionError: () => useRejectionQueryMessagesStore().getRejectionError,
    getRejectionQueryWords: () => useRejectionQueryMessagesStore().getRejectionQueryWords,
    getRejectionQueryTransactionSuccess: () => useRejectionQueryMessagesStore().getRejectionQueryTransactionSuccess,
    geRejectionTransactionError: () => useRejectionQueryMessagesStore().geRejectionTransactionError,

    // Business File Store Getters
    getCompanyInfo: () => useBusinessFileStore().getCompanyInfo,
    getCompanyDirectors: () => useBusinessFileStore().getCompanyDirectors,
    getCompanyShareholders: () => useBusinessFileStore().getCompanyShareholders,
    getCompanySecretary: () => useBusinessFileStore().getCompanySecretary,
    getFilingHistory: () => useBusinessFileStore().getFilingHistory,
    getBusinessFileError: () => useBusinessFileStore().getBusinessFileError,
    getFilingCompliance: () => useBusinessFileStore().getFilingCompliance,
    getCompanySubscribers: () => useBusinessFileStore().getCompanySubscribers,
    getCompanyShareholding: () => useBusinessFileStore().getCompanyShareholding,
    getBusinessPartners: () => useBusinessFileStore().getBusinessPartners,
    getCompanyAddress: () => useBusinessFileStore().getCompanyAddress,
    getCompanyShareClassification: () => useBusinessFileStore().getCompanyShareClassification,
    getCompanyFilingHistory: () => useBusinessFileStore().getCompanyFilingHistory,
    getSendDocumentSuccess: () => useBusinessFileStore().getSendDocumentSuccess,
    getSendDocumentError: () => useBusinessFileStore().getSendDocumentError,
    getBusinessDetailError: () => useBusinessFileStore().getBusinessDetailError,

    // Entity Documents Store Getters
    getBusinessDocuments: () => useEntityDocumentsStore().getBusinessDocuments,
    getCompanyDocuments: () => useEntityDocumentsStore().getCompanyDocuments,
    getSocietyDocuments: () => useEntityDocumentsStore().getSocietyDocuments,
    getEntityDocumentsError: () => useEntityDocumentsStore().getEntityDocumentsError,
    getBusinessNameDocuments: () => useEntityDocumentsStore().getBusinessNameDocuments,
    getBuildingSocietyDocuments: () => useEntityDocumentsStore().getBuildingSocietyDocuments,

    // Legal Documents Registry Store Getters
    getLegalDocuments: () => useLegalDocumentsRegistryStore().getLegalDocuments,
    getLegalDocument: () => useLegalDocumentsRegistryStore().getLegalDocument,
    getLegalDocumentsError: () => useLegalDocumentsRegistryStore().getLegalDocumentsError,
    getLegalDocumentEDMSFile: () => useLegalDocumentsRegistryStore().getLegalDocumentEDMSFile,

    // Practitioners Registry Store Getters
    getPractitioners: () => usePractitionersRegistryStore().getPractitioners,
    getPractitioner: () => usePractitionersRegistryStore().getPractitioner,
    getPractitionersError: () => usePractitionersRegistryStore().getPractitionersError,
    getPractitionerTask: () => usePractitionersRegistryStore().getPractitionerTask,
    getPractitionerMessages: () => usePractitionersRegistryStore().getPractitionerMessages,
    getPractitionerMessageError: () => usePractitionersRegistryStore().getPractitionerMessageError,

    // Business Registry Store Getters
    getBusinessRegistry: () => useBusinessRegistryStore().getBusinessRegistry,
    getCompanyRegistry: () => useBusinessRegistryStore().getCompanyRegistry,
    getBusinessNameRegistry: () => useBusinessRegistryStore().getBusinessNameRegistry,
    getSocietyRegistry: () => useBusinessRegistryStore().getSocietyRegistry,
    getBlacklistRegistry: () => useBusinessRegistryStore().getBlacklistRegistry,
    getDeregistrationRegistry: () => useBusinessRegistryStore().getDeregistrationRegistry,
    getBusinessRegistryError: () => useBusinessRegistryStore().getBusinessRegistryError,
    getEntityFile: () => useBusinessRegistryStore().getEntityFile,
    getEDMSFile: () => useBusinessRegistryStore().getEDMSFile,
    getRegistryError: () => useBusinessRegistryStore().getRegistryError,

    // Internal Processes Store Getters
    getIncorporations: () => useInternalProcessesStore().getIncorporations,
    getRegistrations: () => useInternalProcessesStore().getRegistrations,
    getReservationsProcess: () => useInternalProcessesStore().getReservations,
    getDataCorrections: () => useInternalProcessesStore().getDataCorrections,
    getInternalProcessError: () => useInternalProcessesStore().getInternalProcessError,
    getIncorporationProcessStream: () => useInternalProcessesStore().getIncorporationProcessStream,
    getApprovalActionsStream: () => useInternalProcessesStore().getApprovalActionsStream,
    getIncorporationProcessStats: () => useInternalProcessesStore().getIncorporationProcessStats,
    getStatusChangeSuccess: () => useInternalProcessesStore().getStatusChangeSuccess,
    getStatusChangeError: () => useInternalProcessesStore().getStatusChangeError,
    getInternalProcessStreamError: () => useInternalProcessesStore().getInternalProcessStreamError,
    getRegistrationProcessStream: () => useInternalProcessesStore().getRegistrationProcessStream,
    getAnnualReturnsStatusChange: () => useInternalProcessesStore().getAnnualReturnsStatusChange,
    getAnnualReturnsStatusError: () => useInternalProcessesStore().getAnnualReturnsStatusError,
    getFormComplianceStatusChange: () => useInternalProcessesStore().getFormComplianceStatusChange,
    getFormComplianceStatusError: () => useInternalProcessesStore().getFormComplianceStatusError,

    // Integrations Store Getters
    getMdaIntegrators: () => useIntegrationsStore().getMdaIntegrators,
    getPartnerIntegrators: () => useIntegrationsStore().getPartnerIntegrators,
    getIntegrationSettings: () => useIntegrationsStore().getIntegrationSettings,
    getIntegrationsError: () => useIntegrationsStore().getIntegrationsError,
  },
  actions: {
    // Login Actions
    doLoginUser: ({ }, data) => getLoginStore().doLoginUser(data),
    doOtpVerification: ({ }, data) => getLoginStore().doOtpVerification(data),
    doLogoutUser: ({ }, data) => getLoginStore().doLogoutUser(data),
    doLogOutUser: ({ }, data) => getLoginStore().doLogoutUser(data),
    requestResetCode: ({ }, data) => getLoginStore().requestResetCode(data),
    changePassword: ({ }, data) => getLoginStore().changePassword(data),
    checkTokenVerification: ({ }, data) => getLoginStore().checkTokenVerification(data),
    doLogoutAccount: () => useLoginStore().doLogoutAccount(),
    doInvalidateSession: ({ }, data) => getLoginStore().doInvalidateSession(data),
    getUserProfile: ({ }, token) => getLoginStore().getUserProfile(token),

    // Register Actions
    createStaffUser: ({ }, data) => getRegisterStore().createStaffUser(data),
    createMdaAccount: ({ }, data) => getRegisterStore().createMdaAccount(data),
    createPartnerUser: ({ }, data) => getRegisterStore().createPartnerUser(data),

    // Network Actions
    resetNetworkStatus: () => useNetworkStore().resetNetworkStatus(),

    // Roles Actions
    fetchRoles: () => useRolesStore().fetchRoles(),
    createRole: ({ }, data) => getRolesStore().saveNewRole(data),
    saveNewRole: ({ }, data) => getRolesStore().saveNewRole(data),
    updateRoleAndPermission: ({ }, data) => getRolesStore().updateRoleAndPermission(data),
    fetchPermissions: () => useRolesStore().fetchPermissions(),
    createPermission: ({ }, data) => getRolesStore().addPermission(data),
    addPermission: ({ }, data) => getRolesStore().addPermission(data),
    fetchPermissionGroups: () => useRolesStore().fetchPermissionGroups(),
    createPermissionGroup: ({ }, data) => getRolesStore().addPermissionGroup(data),
    addPermissionGroup: ({ }, data) => getRolesStore().addPermissionGroup(data),
    fetchGroupPermissions: ({ }, groupId) => getRolesStore().fetchGroupPermissions(groupId),
    addPermissionToGroup: ({ }, data) => getRolesStore().addPermissionToGroup(data),
    fetchUserRoles: ({ }, userId) => getRolesStore().fetchUserRoles(userId),
    assignRoleToUser: ({ }, data) => getRolesStore().assignRole(data),
    assignRole: ({ }, data) => getRolesStore().assignRole(data),
    revokeUserRole: ({ }, data) => getRolesStore().removeUserRole(data),
    removeUserRole: ({ }, data) => getRolesStore().removeUserRole(data),

    // Accounts Actions
    fetchUsers: ({ }, query) => getAccountsStore().fetchUsers(query),
    fetchUserAccounts: ({ }, query) => getAccountsStore().fetchUserAccounts(query),
    fetchMdaAccounts: ({ }, query) => getAccountsStore().fetchMdaAccounts(query),
    fetchPartnerAccounts: ({ }, query) => getAccountsStore().fetchPartnerAccounts(query),
    fetchClientAccounts: ({ }, query) => getAccountsStore().fetchClientAccounts(query),
    fetchBusinessAccounts: ({ }, query) => getAccountsStore().fetchBusinessAccounts(query),
    updateUserDetails: ({ }, data) => getAccountsStore().updateUserDetails(data),
    extendUserAccess: ({ }, data) => getAccountsStore().extendUserAccess(data),
    suspendUserAccess: ({ }, data) => getAccountsStore().suspendUserAccess(data),
    revokeUserAccess: ({ }, userId) => getAccountsStore().revokeUserAccess(userId),
    toggleApiAccess: ({ }, data) => getAccountsStore().toggleApiAccess(data),
    fetchAccessAreas: () => useAccountsStore().fetchAccessAreas(),
    grantAccessAreas: ({ }, data) => getAccountsStore().grantAccessAreas(data),
    uploadStaffSignature: ({ }, data) => getAccountsStore().uploadStaffSignature(data),
    fetchStaffSignature: ({ }, userId) => getAccountsStore().fetchStaffSignature(userId),
    searchNira: ({ }, nin) => getAccountsStore().searchNira(nin),
    fetchUserNira: ({ }, userId) => getAccountsStore().fetchUserNira(userId),

    // Transactions Actions
    fetchTransactions: ({ }, query) => getTransactionsStore().fetchTransactions(query),
    filterTransactions: ({ }, filter) => getTransactionsStore().filterTransactions(filter),
    exportTransactions: ({ }, query) => getTransactionsStore().exportTransactions(query),
    retryTransactionVerification: ({ }, data) => getTransactionsStore().retryTransactionVerification(data),
    releasePRNTransaction: ({ }, data) => getTransactionsStore().releasePRNTransaction(data),

    // Departments Actions
    fetchDepartments: () => useDepartmentsStore().fetchDepartments(),
    createDepartment: ({ }, data) => getDepartmentsStore().createDepartment(data),

    // Occupations Actions
    fetchOccupations: () => useOccupationsStore().fetchOccupations(),
    createOccupation: ({ }, data) => getOccupationsStore().createOccupation(data),

    // Countries Actions
    fetchCountries: () => useCountriesStore().fetchCountries(),
    createCountry: ({ }, data) => getCountriesStore().createCountry(data),
    updateCountry: ({ }, data) => getCountriesStore().updateCountry(data),

    // User Manuals Actions
    fetchUserManuals: () => useUserManualsStore().fetchUserManuals(),
    uploadUserManual: ({ }, data) => getUserManualsStore().uploadUserManual(data),
    deleteUserManual: ({ }, id) => getUserManualsStore().deleteUserManual(id),

    // Insolvency Settings Actions
    fetchProfessionalBodies: () => useInsolvencySettingsStore().fetchProfessionalBodies(),
    createProfessionalBody: ({ }, data) => getInsolvencySettingsStore().createProfessionalBody(data),
    updateProfessionalBody: ({ }, data) => getInsolvencySettingsStore().updateProfessionalBody(data),
    fetchProfessionalQualifications: () => useInsolvencySettingsStore().fetchProfessionalQualifications(),
    createProfessionalQualification: ({ }, data) => getInsolvencySettingsStore().createProfessionalQualification(data),
    updateProfessionalQualification: ({ }, data) => getInsolvencySettingsStore().updateProfessionalQualification(data),
    fetchAcademicQualifications: () => useInsolvencySettingsStore().fetchAcademicQualifications(),
    createAcademicQualification: ({ }, data) => getInsolvencySettingsStore().createAcademicQualification(data),
    updateAcademicQualification: ({ }, data) => getInsolvencySettingsStore().updateAcademicQualification(data),
    fetchReasons: () => useInsolvencySettingsStore().fetchReasons(),
    createReason: ({ }, data) => getInsolvencySettingsStore().createReason(data),

    // Service Costs Actions
    fetchServiceCosts: () => useServiceCostsStore().fetchServiceCosts(),
    createServiceCost: ({ }, data) => getServiceCostsStore().createServiceCost(data),
    updateServiceCost: ({ }, data) => getServiceCostsStore().updateServiceCost(data),
    fetchDocumentCosts: () => useServiceCostsStore().fetchDocumentCosts(),
    createDocumentCost: ({ }, data) => getServiceCostsStore().createDocumentCost(data),
    updateDocumentCost: ({ }, data) => getServiceCostsStore().updateDocumentCost(data),
    editDocumentCost: ({ }, data) => getServiceCostsStore().updateDocumentCost(data),
    fetchCurrencies: () => useServiceCostsStore().fetchCurrencies(),

    // Tax Heads Actions
    fetchTaxHeads: () => useTaxHeadsStore().fetchTaxHeads(),
    createTaxHead: ({ }, data) => getTaxHeadsStore().createTaxHead(data),
    updateTaxHead: ({ }, data) => getTaxHeadsStore().updateTaxHead(data),

    // PRN Management Actions
    fetchPrns: ({ }, query) => getPrnManagementStore().fetchPrns(query),
    createPrn: ({ }, data) => getPrnManagementStore().createPrn(data),
    deletePrn: ({ }, data) => getPrnManagementStore().deletePrn(data),
    approvePrn: ({ }, id) => getPrnManagementStore().approvePrn(id),
    declinePrn: ({ }, id) => getPrnManagementStore().declinePrn(id),
    fetchUsedPRNs: ({ }, data) => getPrnManagementStore().fetchUsedPRNs(data),
    filterByPrn: ({ }, data) => getPrnManagementStore().filterByPrn(data),
    addPRNs: ({ }, data) => getPrnManagementStore().addPRNs(data),
    removePRN: ({ }, data) => getPrnManagementStore().removePRN(data),
    uploadPRNs: ({ }, data) => getPrnManagementStore().uploadPRNs(data),

    // Settings Actions
    updateMessenger: ({ }, data) => getSettingsStore().updateMessenger(data),
    fetchAddressByVillageId: ({ }, villageId) => getSettingsStore().fetchAddressByVillageId(villageId),

    // Name Search Actions
    searchBusinessName: ({ }, name) => getNameSearchStore().searchBusinessName(name),
    uploadNameSearch: ({ }, data) => getNameSearchStore().uploadNameSearch(data),

    // Reservations Actions
    fetchReservations: () => useReservationsStore().fetchReservations(),
    updateReservation: ({ }, data) => getReservationsStore().updateReservation(data),
    addReservationExpiryTime: ({ }, data) => getReservationsStore().addReservationExpiryTime(data),
    addReservationNotificationTime: ({ }, data) => getReservationsStore().addReservationNotificationTime(data),
    fetchReservationExpiryTimes: () => useReservationsStore().fetchReservationExpiryTimes(),

    // ISIC Actions
    fetchIsicCategories: () => useIsicStore().fetchIsicCategories(),
    createIsicCategory: ({ }, data) => getIsicStore().createIsicCategory(data),
    updateIsicCategory: ({ }, data) => getIsicStore().updateIsicCategory(data),
    deleteIsicCategory: ({ }, id) => getIsicStore().deleteIsicCategory(id),

    // Dictionary Actions
    fetchDictionaryWords: () => useDictionaryStore().fetchDictionaryWords(),
    createDictionaryWord: ({ }, data) => getDictionaryStore().createDictionaryWord(data),
    updateDictionaryWord: ({ }, data) => getDictionaryStore().updateDictionaryWord(data),
    deleteDictionaryWord: ({ }, id) => getDictionaryStore().deleteDictionaryWord(id),
    fetchDictionaryTypes: () => useDictionaryStore().fetchDictionaryTypes(),

    // Rejection Query Messages Actions
    fetchRejectionMessages: () => useRejectionQueryMessagesStore().fetchRejectionMessages(),
    createRejectionMessage: ({ }, data) => getRejectionQueryMessagesStore().createRejectionMessage(data),
    updateRejectionMessage: ({ }, data) => getRejectionQueryMessagesStore().updateRejectionMessage(data),
    deleteRejectionMessage: ({ }, id) => getRejectionQueryMessagesStore().deleteRejectionMessage(id),
    fetchQueryRejectionWords: () => useRejectionQueryMessagesStore().fetchQueryRejectionWords(),
    addRejectionQueryWord: ({ }, data) => getRejectionQueryMessagesStore().addRejectionQueryWord(data),
    editRejectionQueryWord: ({ }, data) => getRejectionQueryMessagesStore().editRejectionQueryWord(data),
    deleteRejectionQueryWord: ({ }, data) => getRejectionQueryMessagesStore().deleteRejectionQueryWord(data),

    // Business File Actions
    fetchCompanyInfo: ({ }, brn) => getBusinessFileStore().fetchCompanyInfo(brn),
    fetchCompanyDirectors: ({ }, brn) => getBusinessFileStore().fetchCompanyDirectors(brn),
    fetchCompanyShareholders: ({ }, brn) => getBusinessFileStore().fetchCompanyShareholders(brn),
    fetchCompanySecretary: ({ }, brn) => getBusinessFileStore().fetchCompanySecretary(brn),
    fetchFilingHistory: ({ }, brn) => getBusinessFileStore().fetchFilingHistory(brn),
    fetchFilingCompliance: ({ }, brn) => getBusinessFileStore().fetchFilingCompliance(brn),
    fetchCompanySubscribers: ({ }, brn) => getBusinessFileStore().fetchCompanySubscribers(brn),
    fetchCompanyShareholding: ({ }, brn) => getBusinessFileStore().fetchCompanyShareholding(brn),
    fetchBusinessNamePartners: ({ }, brn) => getBusinessFileStore().fetchBusinessNamePartners(brn),
    fetchCompanyAddress: ({ }, brn) => getBusinessFileStore().fetchCompanyAddress(brn),
    fetchCompanyShareClassification: ({ }, brn) => getBusinessFileStore().fetchCompanyShareClassification(brn),
    fetchCompanyFilingHistory: ({ }, brn) => getBusinessFileStore().fetchCompanyFilingHistory(brn),
    sendCompanyDocumentToClient: ({ }, data) => getBusinessFileStore().sendCompanyDocumentToClient(data),
    sendBusinessNameDocumentToClient: ({ }, data) => getBusinessFileStore().sendBusinessNameDocumentToClient(data),
    sendBusinessNameCertificateToClient: ({ }, data) => getBusinessFileStore().sendBusinessNameCertificateToClient(data),

    // Entity Documents Actions
    fetchBusinessDocuments: ({ }, brn) => getEntityDocumentsStore().fetchBusinessDocuments(brn),
    fetchCompanyDocuments: ({ }, brn) => getEntityDocumentsStore().fetchCompanyDocuments(brn),
    fetchSocietyDocuments: ({ }, brn) => getEntityDocumentsStore().fetchSocietyDocuments(brn),
    fetchBusinessNameDocuments: ({ }, data) => getEntityDocumentsStore().fetchBusinessNameDocuments(data),
    fetchBuildingSocietyDocuments: ({ }, data) => getEntityDocumentsStore().fetchBuildingSocietyDocuments(data),

    // Legal Documents Registry Actions
    fetchLegalDocuments: ({ }, query) => getLegalDocumentsRegistryStore().fetchLegalDocuments(query),
    fetchLegalDocument: ({ }, id) => getLegalDocumentsRegistryStore().fetchLegalDocument(id),
    fetchLegalDocumentEDMSFile: ({ }, data) => getLegalDocumentsRegistryStore().fetchLegalDocumentEDMSFile(data),

    // Practitioners Registry Actions
    fetchPractitioners: ({ }, query) => getPractitionersRegistryStore().fetchPractitioners(query),
    fetchPractitioner: ({ }, id) => getPractitionersRegistryStore().fetchPractitioner(id),
    fetchPractitionerTaskMessages: ({ }, id) => getPractitionersRegistryStore().fetchPractitionerTaskMessages(id),

    // Business Registry Actions
    fetchBusinessRegistry: ({ }, query) => getBusinessRegistryStore().fetchBusinessRegistry(query),
    fetchCompanyRegistry: ({ }, query) => getBusinessRegistryStore().fetchCompanyRegistry(query),
    fetchBusinessNameRegistry: ({ }, query) => getBusinessRegistryStore().fetchBusinessNameRegistry(query),
    fetchSocietyRegistry: ({ }, query) => getBusinessRegistryStore().fetchSocietyRegistry(query),
    fetchBlacklistRegistry: ({ }, query) => getBusinessRegistryStore().fetchBlacklistRegistry(query),
    fetchDeregistrationRegistry: ({ }, query) => getBusinessRegistryStore().fetchDeregistrationRegistry(query),
    strikeOffCompany: ({ }, data) => getBusinessRegistryStore().strikeOffCompany(data),
    blacklistCompany: ({ }, data) => getBusinessRegistryStore().blacklistCompany(data),
    deregisterCompany: ({ }, data) => getBusinessRegistryStore().deregisterCompany(data),
    reinstateCompany: ({ }, data) => getBusinessRegistryStore().reinstateCompany(data),
    updateBusinessName: ({ }, data) => getBusinessRegistryStore().updateBusinessName(data),
    fetchCompanyByBRN: ({ }, brn) => getBusinessRegistryStore().fetchCompanyByBRN(brn),
    fetchBusinessNameByBRN: ({ }, brn) => getBusinessRegistryStore().fetchBusinessNameByBRN(brn),
    fetchBuildingSocietyByBRN: ({ }, brn) => getBusinessRegistryStore().fetchBuildingSocietyByBRN(brn),
    fetchCompanyEDMSFile: ({ }, data) => getBusinessRegistryStore().fetchCompanyEDMSFile(data),
    fetchBusinessNameEDMSFile: ({ }, data) => getBusinessRegistryStore().fetchBusinessNameEDMSFile(data),
    fetchSocietyEDMSFile: ({ }, data) => getBusinessRegistryStore().fetchSocietyEDMSFile(data),

    // Internal Processes Actions
    fetchIncorporations: ({ }, query) => getInternalProcessesStore().fetchIncorporations(query),
    fetchRegistrations: ({ }, query) => getInternalProcessesStore().fetchRegistrations(query),
    fetchReservationsProcess: ({ }, query) => getInternalProcessesStore().fetchReservations(query),
    fetchDataCorrections: ({ }, query) => getInternalProcessesStore().fetchDataCorrections(query),
    processApplication: ({ }, data) => getInternalProcessesStore().processApplication(data),
    approveApplication: ({ }, data) => getInternalProcessesStore().approveApplication(data),
    rejectApplication: ({ }, data) => getInternalProcessesStore().rejectApplication(data),
    queryApplication: ({ }, data) => getInternalProcessesStore().queryApplication(data),
    fetchIncorporationProcessesStream: ({ }, data) => getInternalProcessesStore().fetchIncorporationProcessesStream(data),
    fetchIncorporationProcessStatistics: () => useInternalProcessesStore().fetchIncorporationProcessStatistics(),
    fetchIncorporationProcessesPending: () => useInternalProcessesStore().fetchIncorporationProcessesPending(),
    fetchIncorporationProcessesFailed: () => useInternalProcessesStore().fetchIncorporationProcessesFailed(),
    updateProcessStatus: ({ }, data) => getInternalProcessesStore().updateProcessStatus(data),
    runProcessNow: ({ }, data) => getInternalProcessesStore().runProcessNow(data),
    fetchApprovalActions: ({ }, data) => getInternalProcessesStore().fetchApprovalActions(data),
    fetchRegistrationProcessesStream: ({ }, data) => getInternalProcessesStore().fetchRegistrationProcessesStream(data),
    fetchRegistrationProcessesPending: () => useInternalProcessesStore().fetchRegistrationProcessesPending(),
    fetchRegistrationProcessesFailed: () => useInternalProcessesStore().fetchRegistrationProcessesFailed(),
    retryRegistrationProcess: ({ }, data) => getInternalProcessesStore().retryRegistrationProcess(data),
    updateAnnualReturnStatus: ({ }, data) => getInternalProcessesStore().updateAnnualReturnStatus(data),
    updateFormCompliance: ({ }, data) => getInternalProcessesStore().updateFormCompliance(data),

    // Integrations Actions
    fetchMdaIntegrators: () => useIntegrationsStore().fetchMdaIntegrators(),
    fetchPartnerIntegrators: () => useIntegrationsStore().fetchPartnerIntegrators(),
    fetchIntegrationSettings: () => useIntegrationsStore().fetchIntegrationSettings(),
    generateApiKey: ({ }, data) => getIntegrationsStore().generateApiKey(data),
    revokeApiKey: ({ }, id) => getIntegrationsStore().revokeApiKey(id),
    updateIntegrationAccess: ({ }, data) => getIntegrationsStore().updateIntegrationAccess(data),
  },
})
